'use client';
import { Menu, Dialog, Portal } from '@ark-ui/react';
import { useCallback } from 'react';
import { memo } from 'react';
import { UserAvailability } from '~/common/types/user-availability';
import { Button } from '@app/_components/button';
import { Link } from '@app/_components/link';
import { Paragraph, Image } from '@styled-system/jsx';
import { styled } from '@styled-system/jsx';

export type ModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  title: string;
  icon: string;
  note: string;
  buttonText: string;
  followButtonClick: () => void;
  isAuthorFollowed: boolean;
  userAvailability?: UserAvailability;
  reportLink: string;
};

const _FollowRequiredModal = ({
  isOpen,
  onClose,
  title,
  icon,
  note,
  buttonText,
  followButtonClick,
  isAuthorFollowed,
  userAvailability,
  reportLink,
}: ModalProps) => {
  const handleOnOpenChange = useCallback(
    ({ open }: { open: boolean }) => {
      if (!open) {
        onClose?.();
      }
    },
    [onClose]
  );
  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      open={isOpen}
      onOpenChange={handleOnOpenChange}
      closeOnInteractOutside={false}
    >
      <Portal>
        <Backdrop />
        <Positioner>
          <Content minHeight="300px">
            <Image margin="22px auto 0 auto" src={icon} alt={title} />
            <Paragraph
              mt="20px"
              lineHeight="24px"
              fontSize="size_18"
              fontWeight="700"
            >
              こちらの作品は <br />
              {title}公開です。
            </Paragraph>
            <Paragraph
              lineHeight="24px"
              fontSize="size_14"
              fontWeight="400"
              m="22px auto 0 auto"
              color="accent.700"
              maxWidth="90%"
            >
              {note}
            </Paragraph>
            <Button
              maxWidth="90%"
              disabled={
                isAuthorFollowed ||
                userAvailability === UserAvailability.REQUIRE_AUTHOR_FOLLOW_USER
              }
              size="small"
              width="360px"
              height="40px"
              onClick={followButtonClick}
              backgroundColor="tint"
              color="white"
              fontSize="size_16"
              fontWeight="bold"
              margin="32px auto 16px auto"
              _focus={{
                outline: 0,
                outlineColor: 'transparent',
                outlineStyle: 'none',
              }}
            >
              {buttonText}
            </Button>

            {isAuthorFollowed && (
              <Button
                size="small"
                width="360px"
                height="40px"
                onClick={onClose}
                backgroundColor="none"
                color="accent.1000"
                fontSize="size_16"
                fontWeight="bold"
                margin="20px auto 32px auto"
                border="1px solid #313238"
                _focus={{
                  outline: 0,
                  outlineColor: 'transparent',
                  outlineStyle: 'none',
                }}
              >
                閉じる
              </Button>
            )}

            <Menu.Root>
              <MenuTrigger>
                <Paragraph
                  color="accent.700"
                  fontSize="size_14"
                  cursor="pointer"
                  outline="none"
                  border="none"
                  marginBottom="20px"
                >
                  この作品を報告
                </Paragraph>
              </MenuTrigger>
              <Portal>
                <Menu.Positioner>
                  <MenuContent zIndex="teller_modal">
                    <MenuItem id="report" value="report">
                      <Link href={reportLink}>作品カバー画像の転載</Link>
                    </MenuItem>
                  </MenuContent>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          </Content>
        </Positioner>
      </Portal>
    </Dialog.Root>
  );
};

const Backdrop = styled(Dialog.Backdrop, {
  base: {
    backdropFilter: 'blur(20px)',
    background: 'rgba(0, 0, 0, 0.5)',
    height: '100vh',
    left: '0',
    position: 'fixed',
    top: '0',
    width: '100vw',
    zIndex: 'modal_backdrop',
    _open: {
      animation: 'fadein 150ms ease-out',
    },
    _closed: {
      animation: 'fadeout 150ms ease-out',
    },
  },
});

const Positioner = styled(Dialog.Positioner, {
  base: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    position: 'fixed',
    top: {
      base: 0,
      lg: 24,
    },
    bottom: {
      base: 0,
      lg: 24,
    },
    left: {
      base: 0,
      lg: undefined,
    },
    right: {
      base: 0,
      lg: undefined,
    },
    margin: {
      base: 0,
      lg: 'auto',
    },
    height: {
      base: '100dvh',
      lg: '300px',
    },
    width: {
      base: '100dvw',
      lg: '580px',
    },
    zIndex: 'teller_modal',
  },
});

const Content = styled(Dialog.Content, {
  base: {
    background: 'white',
    boxShadow: 'lg',
    textAlign: 'center',
    borderRadius: {
      base: '8px',
    },
    height: 'auto',
    width: '90%',
    _open: {
      animation: 'fadein 150ms ease-out',
    },
    _closed: {
      animation: 'fadeout 150ms ease-out',
    },
  },
});

const MenuTrigger = styled(Menu.Trigger, {
  base: {
    _focus: {
      outline: 0,
      outlineColor: 'transparent',
      outlineStyle: 'none',
    },
  },
});

const MenuContent = styled(Menu.Content, {
  base: {
    bg: 'white',
    w: '200px',
    p: '6px',
    borderRadius: '6px',
    color: 'accent.1000',
    boxShadow: '0 0 48px rgba(0, 0, 0, 0.3)',
    outline: 'none',
    _open: {
      animation: 'fadein 150ms ease-out',
    },
    _closed: {
      animation: 'fadeout 150ms ease-out',
    },
  },
});

const MenuItem = styled(Menu.Item, {
  base: {
    p: '5px',
    cursor: 'pointer',
    lineHeight: '30px',
    borderRadius: '6px',
    color: 'accent.1000',
    _hover: {
      bg: 'strong_bg',
    },
  },
});
export const FollowRequiredModal = memo(_FollowRequiredModal);
